import { useState, useEffect } from 'preact/hooks'
import { useForm } from 'react-hook-form'
import { X, CheckCircle } from 'lucide-preact'
import type { EmailCopyActivationViewDto, WebAssetViewDto } from '@/api/asset'
import { TextBoxField, FormActions } from '@/components'
import { BRAND_CLASSES } from '@/constants/theme'
import { useToast } from '@/hooks/useToast'
import { ASSET_TYPE_META, DEFAULT_ASSET_META, ASSET_STATUS_COLORS } from '../constants/assetMeta'
import { EmailCopyActivationList } from './EmailCopyActivationList'

interface SignOffModalProps {
  isOpen: boolean
  onClose: () => void
  assetDetail: WebAssetViewDto
  onSignOff: (comment?: string) => Promise<void>
  /** Email activations the sign-off moves to the version being signed off (ORB-303). Shown, not chosen: signing off is
   *  what moves them. */
  activations?: EmailCopyActivationViewDto[]
}

interface SignOffFormData {
  SignOffComment: string
}

export function SignOffModal({ isOpen, onClose, assetDetail, onSignOff, activations = [] }: SignOffModalProps) {
  const { showError } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    control,
    handleSubmit,
    reset,
  } = useForm<SignOffFormData>({
    mode: 'onChange',
    defaultValues: { SignOffComment: '' },
  })

  const typeMeta = ASSET_TYPE_META[assetDetail.Type] || DEFAULT_ASSET_META
  const TypeIcon = typeMeta.Icon
  const statusColors = ASSET_STATUS_COLORS[assetDetail.Status] || BRAND_CLASSES.STATUS.DEFAULT

  // ESC key to close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isSubmitting) handleClose()
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, isSubmitting])

  const onSubmit = async (data: SignOffFormData) => {
    setIsSubmitting(true)
    try {
      await onSignOff(data.SignOffComment.trim() || undefined)
      handleClose()
    } catch {
      showError('Sign-off failed', 'An unexpected error occurred during sign-off')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    reset({ SignOffComment: '' })
    setIsSubmitting(false)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto bg-black/50 flex items-center justify-center p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="sign-off-modal-title"
        className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg bg-green-100 dark:bg-green-900/30`}>
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <h2 id="sign-off-modal-title" className="text-xl font-semibold text-slate-900 dark:text-white">
              Sign Off Asset
            </h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close modal"
            className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Asset Info Card */}
            <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600">
              {/* Type Icon */}
              <div className={`flex-shrink-0 p-2 rounded-lg ${typeMeta.badgeBg}`}>
                <TypeIcon className={`w-5 h-5 ${typeMeta.color}`} />
              </div>

              {/* Name + Type */}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                  {assetDetail.Name}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {assetDetail.Type}
                </p>
              </div>

              {/* Status Badge */}
              <span className={`flex-shrink-0 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${statusColors}`}>
                {assetDetail.Status}
              </span>
            </div>

            {activations.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  {activations.length === 1 ? 'This activation sends' : 'These activations send'} v{assetDetail.CurrentVersionNumber} from now on:
                </p>
                <EmailCopyActivationList activations={activations} />
              </div>
            )}

            {/* Sign-Off Comment */}
            <TextBoxField
              control={control}
              name="SignOffComment"
              label="Sign-Off Comment"
              multiline
              rows={3}
              placeholder="Add an optional comment for this sign-off..."
              rules={{
                validate: (v) => !v || v.trim().length > 0 || 'Sign-off comment cannot be empty',
                maxLength: { value: 2000, message: 'Sign-off comment cannot exceed 2000 characters' },
              }}
            />
          </div>

          {/* Footer */}
          <FormActions
            isSubmitting={isSubmitting}
            isSaveDisabled={isSubmitting}
            onCancel={handleClose}
            saveText={isSubmitting ? 'Signing Off...' : 'Sign Off'}
          />
        </form>
      </div>
    </div>
  )
}
